import { Subject } from "rxjs";
import { filter, map } from "rxjs/operators";
import { getAppStatus, registerApplication, start } from "single-spa";
import toastr from "toastr";
import {
  productApplications,
  productOnboardingApps,
  routesStartsWithToShowSideNav,
  tinggAppsBaseRoutes,
} from "./utils";
import { captureFirstMount } from "./audit-logs.config";

// Importing will automatically polyfill window.fetch and related APIs:
import "whatwg-fetch";

// @ts-ignore
export class EventBusService {
  subject$: Subject<any>;

  constructor() {
    this.subject$ = new Subject();
  }

  emit(event: any) {
    this.subject$.next(event);
  }

  on(eventName: any, action: any) {
    return this.subject$
      .pipe(
        filter((e: any) => e.name === eventName),
        map((e) => e["data"])
      )
      .subscribe(action);
  }
}

/* GLOBALS */
var EventBus = new EventBusService();
const currentPathName = window.location.pathname.split("/")[1];

checkIfCanAccessApplication(); // First determine user if logged in

//Register apps that are NOT dependent on side-nav
registerApplication({
  name: "@tingg/cas-ui-app",
  app: () => System.import("@tingg/cas-ui-app") as any,
  activeWhen: ["/cas"],
  customProps: (name, location) => ({
    domElement: document.getElementById("div-container"),
  }),
});
registerApplication({
  name: "@tingg/signup",
  app: () => System.import("@tingg/signup") as any,
  activeWhen: ["/signup"],
  customProps: (name, location) => ({
    domElement: document.getElementById("div-container"),
  }),
});
//
// registerApplication({
//   name: "@tingg/onboarding",
//   app: () => loadApp("@tingg/onboarding", "/onboarding", "Dashboard"),
//   activeWhen: ["/onboarding"],
//   customProps: (name, location) => ({
//     domElement: document.getElementById("div-container"),
//   }),
// });

/*
 * Register apps, that don't need the side nav(product onboarding pages),
 * ->when route to app is visited
 * -> Other apps are registered after side nav
 * ->
 */
(function registerProductOnboardingAppsWhenOnExactRoute() {
  //check current route is an onboarding route && get data
  const routeToRegisterConfig = productOnboardingApps.find((app) => {
    return app.routes.find((route) => route == window.location.pathname);
  });
  if (!routeToRegisterConfig) {
    return false;
  }

  if (routeToRegisterConfig?.appName) {
    const app = productApplications.find(
      (p) => p.appName == routeToRegisterConfig.appName
    );
    registerApplication({
      name: app.appName,
      app: () => loadApp(app.appName, app.activeWhen, app.codeName),
      activeWhen: [app.activeWhen],
      customProps: (name, location) => ({
        domElement: document.getElementById("div-container"),
        EventBus: EventBus, // For sharing data microsite into another microsite.
      }),
    });
  }
})();

/*
 * Register apps, that don't need the side nav(product onboarding pages),
 * -> when visit signup route
 * -> Check product onboarding for to determine app to mount
 * ->
 */
function preloadProductOnboardingAppWhenOnSignupRoute() {
  const productCode = JSON.parse(localStorage.getItem("signupData"))?.signup
    ?.product;

  //Function should work when on signup pages & already know product one is onboarding for
  if (window.location.pathname.split("/")[1] != "signup" || !productCode) {
    return false;
  }

  const routeToRegisterConfig = productOnboardingApps.find(
    (app) => app.productCode == productCode
  );

  if (!routeToRegisterConfig) {
    return false;
  }

  //If it's not mounted, pre register it
  if (
    routeToRegisterConfig?.appName &&
    (getAppStatus(routeToRegisterConfig?.appName) == null ||
      getAppStatus(routeToRegisterConfig?.appName) == null)
  ) {
    const app = productApplications.find(
      (p) => p.appName == routeToRegisterConfig.appName
    );

    /*
     * TODO: RETHINK THIS HACK
     *  -> Below hack quickly takes one to product page then back to where they were
     *  -> This helps to register the app
     */

    // setTimeout(() => {
    //   const originalpath = window.location.pathname;
    //   window.history.pushState(null, null, routeToRegisterConfig.routes[0]);
    //   setTimeout(() => {
    //     window.history.pushState(null, null, originalpath);
    //   });

    //   registerApplication({
    //     name: app.appName,
    //     app: () => loadApp(app.appName, app.activeWhen, app.codeName),
    //     activeWhen: [app.activeWhen],
    //     customProps: (name, location) => ({
    //       domElement: document.getElementById("div-container"),
    //       EventBus: EventBus,
    //     }),
    //   });
    // }, 3000);
  }
}

// Function called to determine when the side nav is to be registered
function determineShowSideNav(location) {
  let showSideNav = false;

  // Check if current route starting is one that can show side nav
  routesStartsWithToShowSideNav.forEach((route) => {
    if (location && location.pathname.startsWith(route)) {
      showSideNav = true;
    }
  });

  // Hide sidebar for product onboarding pages
  productOnboardingApps.some((app) => {
    return app.routes.some((route) => route == location?.pathname);
  }) && (showSideNav = false);

  return showSideNav;
}

registerApplication({
  name: "@tingg/side-navigation-ui-app",
  app: () => loadSideNavApp(),
  activeWhen: determineShowSideNav,

  customProps: (name, location) => ({
    domElement: document.getElementById("nav-container"),
    EventBus: EventBus,
  }),
});

function loadSideNavApp() {
  return new Promise<any>((resolve, reject) => {
    System.import("@tingg/side-navigation-ui-app")
      .then((sideNavApp) => {
        //Register top-nav after side nav
        registerApplication({
          name: "@tingg/top-nav",
          app: () => System.import("@tingg/top-nav") as any,
          activeWhen: ["/"],
          customProps: (name, location) => ({
            domElement: document.getElementById("topnav-container"),
            EventBus: EventBus,
          }),
        });

        //Check if permissions are loaded, before registering product applications
        let tInstance = setTimeout(() => {
          toastr.info(
            ` <span style="font-size:17px">
                Oops, The application resources are taking too long to load. Please refresh or try again later.
              </span>
            `,
            "",
            {
              toastClass: "tingg-root-toast",
              timeOut: "0",
              extendedTimeOut: "0",
              closeButton: true,
              tapToDismiss: false,
              preventDuplicates: true,
              progressBar: true,
              hideMethod: "fadeOut",
            }
          );
        }, 60000);

        //Determine if permissions are ready
        new Promise<boolean>((resolve) => {
          const intCheckPerm = setInterval(() => {
            if (localStorage.getItem("Permissions")) {
              clearInterval(intCheckPerm);
              resolve(true);
            }
          }, 500);
        }).then((res) => {
          clearTimeout(tInstance);
          registerProductApplications();
        });

        //Resolve sidenav
        resolve(sideNavApp);
      })
      .catch((error) => {
        console.log("Exception trace->", error);
        reject(error);
      });
  });
}

function registerProductApplications() {
  /**
   * Because of permissions, wait for side nav
   */
  productApplications.forEach((app) => {
    registerApplication({
      name: app.appName,
      app: () => loadApp(app.appName, app.activeWhen, app.codeName),
      activeWhen: [app.activeWhen],
      customProps: (name, location) => ({
        domElement: document.getElementById("div-container"),
        EventBus: EventBus, // For sharing data microsite into another microsite.
      }),
    });
  });
}

start();

/*
 * UTILITY FUNCTIONS
 */
function loadApp(
  applicationName: string,
  activeWhen: string,
  codeName: string
) {
  toastr.clear();
  //scroll top
  document.body.scrollTop = document.documentElement.scrollTop = 0;

  return new Promise<any>((resolve, reject) => {
    const urlFirstPathName = window.location.pathname.split("/")[1];
    const currentUrl = "/" + urlFirstPathName;

    System.import(applicationName.toString())
      .then((app) => {
        resolve(app);
        // resolve(System.import(applicationName.toString()));
      })
      .catch((error) => {
        console.log("Exception trace->", error);
        if (activeWhen === currentUrl) {
          setTimeout(() => {
            toastr.info(
              ` <span style="font-size:17px">
                  Oops, <strong style="font-size:19px"> ${codeName} </strong> is taking too long to load. Please refresh or try again later.
                </span>
              `,
              "", //title
              {
                toastClass: "tingg-root-toast",
                timeOut: "0",
                extendedTimeOut: "0",
                closeButton: true,
                tapToDismiss: false,
                // "positionClass": "toast-top-full-width",
                preventDuplicates: true,
                progressBar: true,
                hideMethod: "fadeOut",
              }
            );
          }, 1500);
        }
        resolve(false);
      });
  });
}

function removeBrandLogo() {
  const logoDiv = document.querySelector(`#brand-logo`);
  logoDiv["style"].display = "none";
  const mainApp = document.querySelector(`main`);
  mainApp.removeAttribute("style");
}

function checkIfBaseRouteExists() {
  if (
    currentPathName !== "" &&
    !tinggAppsBaseRoutes.includes(currentPathName)
  ) {
    removeBrandLogo();

    let iframe404 = document.querySelector(".iframe-404");
    iframe404["style"].display = "block";
    iframe404.setAttribute("src", "/assets/error404.html");

    let otherApp = document.querySelector("main");
    otherApp["style"].display = "none";
    window.scrollTo(0, document.body.scrollHeight); //scroll to bottom
  }
}

function checkIfCanAccessApplication() {
  const userData = JSON.parse(localStorage.getItem("User"));
  const applicationPathName = window.location.pathname.split("/")[1];
  const secondPathName = window.location.pathname.split("/")[2];

  /*
   * Allow unauthorized access only if accessing CREATE_ACCOUNTS & SIGNUPS ,
   * -> else redirect user to CAS login.
   */
  const productOnboardingRoutes = [
    "create-account",
    "onboarding",
    "test-onboarding",
    "create-onbording-store",
    "trial-counter",
    "setup-store",
  ];

  //Has the word signup on route / is a product onboarding page
  if (
    ["signup"].includes(applicationPathName.toLowerCase()) ||
    (secondPathName &&
      productOnboardingRoutes.includes(secondPathName.toLowerCase()))
  ) {
    return true;
  }

  if (!userData && currentPathName != "cas") {
    let url = "";
    if (currentPathName) {
      url = `${
        location.origin
      }/cas/auth?response_type=code&client_id=onboarding&redirect_uri=${encodeURIComponent(
        document.location.href
      )}&state=123333&nonce=9877666&scope=`;
    } else {
      url = location.origin + "/cas/login";
    }
    window.location.href = url;
  }

  //If user hits location origin
  if (!currentPathName && userData) {
    document.location.href = location.origin + "/onboarding/dashboard";
  }
}

/*
 ******** EVENT LISTENERS *********
 */
// LISTEN TO FIRST APP MOUNT
window.addEventListener("single-spa:first-mount", () => {
  captureFirstMount();
  removeBrandLogo();
});

// LISTEN TO ANY ROUTE CHANGE
window.addEventListener("single-spa:routing-event", (event) => {
  checkIfCanAccessApplication();
  preloadProductOnboardingAppWhenOnSignupRoute();

  // is a mounting event and not a PopState Event
  if (event["detail"].newAppStatuses && !event["detail"].originalEvent) {
    checkIfBaseRouteExists();
  }
});

window.addEventListener("beforeunload", (event) => {
  localStorage.removeItem("Permissions");
});

//initAuditLogs();
